import { FormattedMessage, history, useIntl } from "@umijs/max";
import { Button, Checkbox, Form, message, Modal } from "antd";
import { FC, useEffect, useState } from "react";
import { useDebounceFn, useRequest } from "ahooks";

import styles from "../index.less";

import InputNumber from "../components/InputNumber";
import SeatTable from "../components/SeatTable";
import SelectSeat from "../components/SelectSeat";
import {
  calcAmountService,
  ConsumptionType,
  createOrderService,
  OrderInfo,
  PayType,
  postOrderSeatGoodsService,
} from "@/services/order";
import { ResponseCode } from "@/constants";
import PayModal from "@/components/PayModal";
import { ServicePaymentAgreementUrl } from "@packages/pricing-components";

interface BuyOrRenewSeatModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  type: "buy" | "renew";
  defaultSelectedSeatId?: string;
  onClose?: () => void;
}

const BuyOrRenewSeatModal: FC<BuyOrRenewSeatModalProps> = ({
  open,
  setOpen,
  type,
  onClose,
  defaultSelectedSeatId,
}) => {
  const intl = useIntl();

  const [form] = Form.useForm<{
    count: number;
    duration: { id: string; amount: number };
    seatIds: string[];
  }>();
  const count = Form.useWatch("count", form);
  const duration = Form.useWatch("duration", form);
  const seatIds = Form.useWatch("seatIds", form);

  const { data: seat } = useRequest(
    async () => {
      if (open) {
        const rsp = await postOrderSeatGoodsService(
          type === "buy" ? ConsumptionType.FIRST : ConsumptionType.RENEW
        );
        if (rsp.code !== ResponseCode.Success) {
          message.error(rsp.msg);
          return [];
        }

        return rsp.data ?? [];
      }

      return [];
    },
    {
      refreshDeps: [type, open],
    }
  );

  const [agreePolicy, setAgreePolicy] = useState<boolean>(false);
  const [openPayModal, setOpenPayModal] = useState<boolean>(false);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();

  const resetStates = () => {
    form.resetFields();
    setAgreePolicy(false);
    setOrderInfo(void 0);
  };

  useEffect(() => {
    if (open && defaultSelectedSeatId) {
      form.setFieldsValue({
        seatIds: [defaultSelectedSeatId],
      });
    }
  }, [open, defaultSelectedSeatId]);

  useEffect(() => {
    if (open && seat && seat.length > 0) {
      form.setFieldsValue({
        duration: {
          id: seat[0].goodsId!,
          amount: seat[0].amount,
        },
      });
    }
  }, [open, seat]);

  const { data: previewPrice } = useRequest(
    async () => {
      try {
        if (open) {
          const values = await form.validateFields();

          const rsp = await calcAmountService(
            type === "buy"
              ? {
                  seatNum: values.count,
                  seatDuration: values.duration,
                  consumptionType: ConsumptionType.FIRST,
                }
              : {
                  seatIds: values.seatIds,
                  seatDuration: values.duration,
                  consumptionType: ConsumptionType.RENEW,
                }
          );

          if (rsp.code !== ResponseCode.Success) {
            message.error(rsp.msg);
            return;
          }

          return rsp?.data?.amount ?? "--";
        }
      } catch (error) {
        console.error(error);
        return;
      }
    },
    {
      refreshDeps: [open, count, duration, seatIds],
      debounceWait: 100,
    }
  );

  const handleBuyOrRenew = useDebounceFn(
    async () => {
      if (!agreePolicy) {
        message.info(
          intl.formatMessage({
            id: "components.trading-system.common.message.warn-check-policy",
          })
        );
        return;
      }

      try {
        const values = await form.validateFields();

        const rsp = await createOrderService(
          type === "buy"
            ? {
                seatNum: values.count,
                seatDuration: values.duration,
                consumptionType: ConsumptionType.FIRST,
              }
            : {
                seatIds: values.seatIds,
                seatDuration: values.duration,
                consumptionType: ConsumptionType.RENEW,
              }
        );

        if (rsp.code !== ResponseCode.Success) {
          message.error(rsp.msg);
          return;
        }

        if (rsp.data && rsp.data.amount > 0) {
          // 干掉当前弹窗
          resetStates();
          setOpen(false);
          // 唤醒支付
          setOrderInfo({
            orderId: rsp.data.orderId,
            amount: rsp.data.amount,
          });
          setOpenPayModal(true);
        }
      } catch (error) {
        console.error(error);
      }
    },
    {
      wait: 200,
    }
  );

  return (
    <>
      <Modal
        width={720}
        classNames={{
          content: styles["ant-modal-content-wrapper"],
          header: styles["ant-modal-header-wrapper"],
          body: styles["ant-modal-body-wrapper"],
          footer: styles["ant-modal-footer-wrapper"],
        }}
        open={open}
        title={intl.formatMessage({
          id:
            type === "buy"
              ? "components.buy-or-renew-seat-modal.title.buy"
              : "components.buy-or-renew-seat-modal.title.renew",
        })}
        destroyOnClose
        maskClosable={false}
        onCancel={() => {
          resetStates();
          setOpen(false);
          onClose?.();
        }}
        onClose={() => {
          resetStates();
          setOpen(false);
          onClose?.();
        }}
        footer={
          <div className="flex flex-row justify-end items-center">
            <div className="flex flex-col justify-start items-end text-text-regular">
              <div className="flex flex-row items-end">
                <FormattedMessage
                  id="components.trading-system.common.selected"
                  values={{
                    num: count ?? seatIds?.length ?? 0,
                  }}
                />
                <div className="flex flex-row justify-end items-end text-[#EC7057] ml-[4px] leading-none">
                  <div className="text-[14px]">¥</div>
                  <div className="text-[24px] font-[DIN]">
                    {previewPrice ?? "--"}
                  </div>
                </div>
              </div>
              <div className="flex flex-row justify-end items-center">
                <Checkbox
                  className="mr-1"
                  checked={agreePolicy}
                  onChange={(e) => setAgreePolicy(e.target.checked)}
                />
                <div className="text-text-regular">
                  <FormattedMessage id="components.trading-system.common.agreement" />
                </div>
                <a
                  href={ServicePaymentAgreementUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="components.trading-system.common.agreement.policy" />
                </a>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              className="ml-[24px] w-[180px] h-[42px]"
              onClick={() => handleBuyOrRenew.run()}
            >
              <FormattedMessage id="components.buy-or-renew-seat-modal.action.buy" />
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          {/* 购买席位数 */}
          {type === "buy" && (
            <Form.Item
              name="count"
              initialValue={1}
              label={
                <div className={styles["ant-form-label"]}>
                  <FormattedMessage id="components.buy-or-renew-seat-modal.form.item.buy.seat-count" />
                </div>
              }
              required
              rules={[{ required: true, message: "请输入购买数量" }]}
            >
              <InputNumber
                min={0}
                precision={0}
                rootClassName={styles["number-input"]}
              />
            </Form.Item>
          )}

          <Form.Item
            name="duration"
            label={
              <div className={styles["ant-form-label"]}>
                <FormattedMessage
                  id={
                    type === "buy"
                      ? "components.buy-or-renew-seat-modal.form.item.buy.duration"
                      : "components.buy-or-renew-seat-modal.form.item.renew.duration"
                  }
                />
              </div>
            }
            required
            rules={[
              {
                required: true,
                message: type === "buy" ? "请选择购买时长" : "请选择续费时长",
              },
            ]}
          >
            <SelectSeat seats={seat} />
          </Form.Item>

          {type === "renew" && (
            <Form.Item
              name="seatIds"
              label={
                <div className={styles["ant-form-label"]}>
                  <FormattedMessage id="components.buy-or-renew-seat-modal.form.item.renew.select-seat" />
                </div>
              }
            >
              <SeatTable />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <PayModal
        open={openPayModal}
        onClose={() => {
          setOpenPayModal(false);
        }}
        orderInfo={orderInfo}
        onSuccess={(payType) => {
          setOpenPayModal(false);

          if (payType !== PayType.OFFLINE) {
            location.reload();
            return;
          }

          // 线下支付跳我的订单
          history.replace("/resource/order");
        }}
      />
    </>
  );
};

export default BuyOrRenewSeatModal;
